import React from 'react';

import {
  Button,
  Divider,
  Grid,
  Stack,
  styled
} from '@mui/material';
import {
  BaseModal, errorUtil,
  fields, Form, formUtil,
  validationUtil
} from 'iron-forge-react-modules';

import { appFormUtil } from 'modules';
import { useForm } from 'react-hook-form';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const RootBox = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2)
}));



export interface IProps {
  accountId?: string;

  onClose: () => void;
  onSave: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.accountId);
  const [saveThirdPartyAccount, saveThirdPartyAccountTuple] = dataService.useSaveThirdPartyAccount(props.accountId);
  const [deleteThirdPartyAccount, deleteThirdPartyAccountTuple] = dataService.useDeleteThirdPartyAccount(props.accountId);
  const form = useForm<types.IFormState>({
    defaultValues: {
      name: '',
      accountNumber: '',
      carrierType: '',
      phoneNumber: '',
      attention: '',
      addressee: '',
      street1: '',
      street2: '',
      street3: '',
      city: '',
      postalCode: '',
      usState: '',
      countryCode: ''
    }
  });

  formUtil.useSetFormFields(queryTuple, form.setValue, [
    'accountNumber',
    'name',
    'carrierType',
    'attention',
    'addressee',
    'street1',
    'street2',
    'street3',
    'city',
    'postalCode',
    'usState',
    'countryCode',
    'phoneNumber',
    'countryCode'
  ]);



  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      errorUtil.errorWrap(async () => {
        const trimmedFormState = appFormUtil.trimFormStateValues(formState);
        await saveThirdPartyAccount(trimmedFormState);
        props.onSave();
      }, {
        INVALID_ARGS: "Some form fields were invalid. Check the values and resubmit.",
        DUPLICATE_ACCOUNT_FOUND: "A duplicate account was found with this name and account number."
      });
    },
    [props, saveThirdPartyAccount]
  );

  const handleClickDelete = React.useCallback(
    async () => {
      if (window.confirm("Are you sure you want to delete this account? This action is irreversible!")) {
        await deleteThirdPartyAccount();
        props.onSave();
      }
    },
    [props, deleteThirdPartyAccount]
  );



  const loading = queryTuple.loading || saveThirdPartyAccountTuple.loading || deleteThirdPartyAccountTuple.loading;
  const isEditing = props.accountId && props.accountId !== '*';

  const children = React.useCallback(() => {
    return (
      <RootBox>
        <Form.FC
          disableDefaultOverflow
          onSubmit={form.handleSubmit(processSubmit)}>
          <Grid
            container
            spacing={1}
            rowSpacing={2}>
            <Grid
              item
              xs={12}
              md={12}>
              <ControlledTextField
                disableDefaultMargins
                isRequired
                isDisabled={loading}
                fieldKey="name"
                label="Account Name"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <ControlledTextField
                disableDefaultMargins
                isRequired
                isDisabled={loading}
                fieldKey="accountNumber"
                label="Account Number"
                formControl={form.control} />
            </Grid>

            <Grid
              item xs={12}
              md={6}>
              <ControlledTextField
                disableDefaultMargins
                isRequired
                isDisabled={loading}
                label="Carrier Type"
                fieldKey="carrierType"
                formControl={form.control} />
            </Grid>

            <Grid
              item xs={12}
              md={6}>
              <ControlledTextField
                disableDefaultMargins
                isRequired
                isDisabled={loading}
                label="Attention"
                fieldKey="attention"
                formControl={form.control} />
            </Grid>

            <Grid
              item xs={12}
              md={6}>
              <ControlledTextField
                disableDefaultMargins
                isRequired
                isDisabled={loading}
                label="Addressee"
                fieldKey="addressee"
                formControl={form.control} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid
              item
              xs={12}
              md={8}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                isRequired
                label="Street 1"
                fieldKey="street1"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}
              md={4}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                label="Unit/Apt"
                fieldKey="street2"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}
              md={12}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                label="Street 3"
                fieldKey="street3"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                isRequired
                label="City"
                fieldKey="city"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={4}
              md={2}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                label="State"
                fieldKey="usState"
                isRequired
                extraValidationRules={{
                  maxLength: 2
                }}
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={8}
              md={4}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                isRequired
                label="Zip Code"
                fieldKey="postalCode"
                extraValidationRules={{ pattern: validationUtil.reZipCodeShortOrLong }}
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}
              md={4}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                isRequired
                label="Country Code"
                fieldKey="countryCode"
                extraValidationRules={{
                  maxLength: 2
                }}
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}
              md={8}>
              <ControlledTextField
                disableDefaultMargins
                isDisabled={loading}
                isRequired
                label="Phone Number"
                fieldKey="phoneNumber"
                extraValidationRules={{ pattern: validationUtil.rePhoneNumberUsaDefault }}
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={12}>
              <Stack
                direction="row"
                justifyContent={isEditing ? 'space-between' : 'flex-end'}>
                {isEditing && (
                  <Button
                    color="error"
                    onClick={handleClickDelete}>
                    Delete
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained">
                  Save
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Form.FC>
      </RootBox>
    );
  }, [
    form,
    isEditing,
    loading,
    processSubmit,
    handleClickDelete
  ]);

  return (
    <BaseModal.FC
      isOpen
      title="Save Third Party Account"
      onClose={props.onClose}>
      {children}
    </BaseModal.FC>
  );
};
